import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import noteService from '../services/noteService';
import taskService from '../services/taskService';
import { useAuth } from './AuthContext';

const NoteContext = createContext();

export const useNotes = () => useContext(NoteContext);

export const NoteProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'updatedAt',
    sortOrder: 'desc',
    category: 'all'
  });

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await noteService.getNotes();
      console.log("Notes response:", response);
      setNotes(response.notes);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Failed to load notes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const categoriesData = await noteService.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  // Load notes and categories when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotes();
      fetchCategories();
    } else {
      // Clear data when not authenticated
      setNotes([]);
      setFilteredNotes([]);
      setCategories([]);
    }
  }, [isAuthenticated, fetchNotes, fetchCategories]);

  // Apply filtering whenever filters or notes change
  useEffect(() => {
    applyFilters();
  }, [filters, notes, categories]);

  const getNoteById = useCallback(async (noteId) => {
    setLoading(true);
    setError(null);
    try {
      return await noteService.getNoteById(noteId);
    } catch (err) {
      console.error(`Error fetching note ${noteId}:`, err);
      setError(`Failed to load note ${noteId}. Please try again.`);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Alias for getNoteById to match the function name used in the NoteEditScreen
  const fetchNoteById = useCallback(async (noteId) => {
    return await getNoteById(noteId);
  }, [getNoteById]);

  const getPublicNoteById = useCallback(async (noteId) => {
    setLoading(true);
    setError(null);
    try {
      // Use the service method for public notes
      return await noteService.getPublicNote(noteId);
    } catch (err) {
      console.error(`Error fetching public note ${noteId}:`, err);
      setError('This note is private or does not exist');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getSharedNote = useCallback(async (shareToken) => {
    setLoading(true);
    setError(null);
    try {
      return await noteService.getSharedNote(shareToken);
    } catch (err) {
      console.error(`Error fetching shared note with token ${shareToken}:`, err);
      setError('Failed to load shared note. It may have been removed or unshared.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createNote = useCallback(async (noteData) => {
    setLoading(true);
    setError(null);
    try {
      const newNote = await noteService.createNote(noteData);
      setNotes(prevNotes => [...prevNotes, newNote]);
      return newNote;
    } catch (err) {
      console.error('Error creating note:', err);
      setError('Failed to create note. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateNote = useCallback(async (noteData) => {
    setLoading(true);
    setError(null);
    try {
      // Check if this is a new note or an existing one
      if (!noteData.id) {
        // This is a new note
        const newNote = await noteService.createNote(noteData);
        setNotes(prevNotes => [...prevNotes, newNote]);
        return newNote;
      } else {
        // This is an existing note
        const updatedNote = await noteService.updateNote(noteData.id, noteData);
        setNotes(prevNotes => 
          prevNotes.map(note => 
            note.id === noteData.id ? updatedNote : note
          )
        );
        return updatedNote;
      }
    } catch (err) {
      console.error(`Error updating note:`, err);
      setError('Failed to update note. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteNote = useCallback(async (noteId) => {
    setLoading(true);
    setError(null);
    try {
      await noteService.deleteNote(noteId);
      setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
      return true;
    } catch (err) {
      console.error(`Error deleting note ${noteId}:`, err);
      setError('Failed to delete note. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const shareNote = useCallback(async (noteId) => {
    setLoading(true);
    setError(null);
    try {
      const updatedNote = await noteService.shareNote(noteId);
      setNotes(prevNotes => 
        prevNotes.map(note => 
          note.id === noteId ? updatedNote : note
        )
      );
      return updatedNote;
    } catch (err) {
      console.error(`Error sharing note ${noteId}:`, err);
      setError('Failed to share note. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const unshareNote = useCallback(async (noteId) => {
    setLoading(true);
    setError(null);
    try {
      const updatedNote = await noteService.unshareNote(noteId);
      setNotes(prevNotes => 
        prevNotes.map(note => 
          note.id === noteId ? updatedNote : note
        )
      );
      return updatedNote;
    } catch (err) {
      console.error(`Error unsharing note ${noteId}:`, err);
      setError('Failed to unshare note. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const togglePublic = useCallback(async (noteId, currentState) => {
    setLoading(true);
    setError(null);
    try {
      const updatedNote = await noteService.togglePublic(noteId, currentState);
      setNotes(prevNotes => 
        prevNotes.map(note => 
          note.id === noteId ? updatedNote : note
        )
      );
      return updatedNote;
    } catch (err) {
      console.error(`Error toggling public status for note ${noteId}:`, err);
      setError('Failed to update note visibility. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createGuestNote = useCallback(async (noteData) => {
    setLoading(true);
    setError(null);
    try {
      return await noteService.createGuestNote(noteData);
    } catch (err) {
      console.error('Error creating guest note:', err);
      setError('Failed to create guest note. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const connectNoteToTask = useCallback(async (noteId, taskId) => {
    setLoading(true);
    setError(null);
    try {
      await taskService.connectNoteToTask(taskId, noteId);
      // Update the note in the state with the taskId
      setNotes(prevNotes => 
        prevNotes.map(note => 
          note.id === noteId ? { ...note, taskId } : note
        )
      );
      return true;
    } catch (err) {
      console.error(`Error connecting note ${noteId} to task ${taskId}:`, err);
      setError('Failed to connect note to task. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const disconnectNoteFromTask = useCallback(async (noteId, taskId) => {
    setLoading(true);
    setError(null);
    try {
      await taskService.disconnectNoteFromTask(taskId);
      // Update the note in the state to remove taskId
      setNotes(prevNotes => 
        prevNotes.map(note => 
          note.id === noteId ? { ...note, taskId: null } : note
        )
      );
      return true;
    } catch (err) {
      console.error(`Error disconnecting note ${noteId} from task:`, err);
      setError('Failed to disconnect note from task. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFilters = () => {
    let filtered = [...notes];
    
    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(note => 
        note.title.toLowerCase().includes(searchLower) || 
        (note.content && note.content.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply category filter
    if (filters.category !== 'all') {
      filtered = filtered.filter(note => note.categoryId === filters.category);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[filters.sortBy];
      const bValue = b[filters.sortBy];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return filters.sortOrder === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      } else {
        return filters.sortOrder === 'asc' 
          ? aValue - bValue 
          : bValue - aValue;
      }
    });
    
    // Add category details to notes
    filtered = filtered.map(note => {
      if (note.categoryId) {
        const categoryDetails = categories.find(cat => cat.id === note.categoryId);
        return { ...note, categoryDetails };
      }
      return note;
    });
    
    setFilteredNotes(filtered);
  };
  
  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };
  
  const searchTasks = useCallback(async (query) => {
    try {
      // Call the API to search for tasks
      return await taskService.searchTasks(query);
    } catch (err) {
      console.error('Error searching tasks:', err);
      setError('Failed to search tasks. Please try again.');
      throw err;
    }
  }, []);

  const contextValue = {
    notes,
    filteredNotes,
    categories,
    loading,
    error,
    filters,
    fetchNotes,
    getNoteById,
    fetchNoteById,
    getPublicNoteById,
    getSharedNote,
    createNote,
    updateNote,
    deleteNote,
    shareNote,
    unshareNote,
    togglePublic,
    createGuestNote,
    connectNoteToTask,
    disconnectNoteFromTask,
    searchTasks,
    updateFilters
  };

  return <NoteContext.Provider value={contextValue}>{children}</NoteContext.Provider>;
};

export default NoteContext; 